<template>
  <div class="location-check-in-settings__wrapper">
    <ui-loading
      v-if="loading.data"
      class="location-check-in-settings__loader"
    />

    <validation-observer
      v-else
      tag="div"
      v-slot="{ validate }"
      class="location-check-in-settings"
    >
      <validation-provider
        tag="div"
        :name="$t('form.labels.checkoutSettings')"
        rules="required"
        mode="passive"
        v-slot="{ errors }"
        class="location-check-in-settings__field"
      >
        <ui-select
          v-model="form.checkOutSettings"
          :items="CHECKOUT_VARIANTS"
          :label="$t('form.labels.checkoutSettings')"
          :placeholder="$t('form.placeholders.select')"
          label-key="label"
          value-key="value"
          filterable
          required
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        tag="div"
        :name="$t('form.labels.enableSelfCheckIn')"
        mode="passive"
        v-slot="{ errors }"
        class="location-check-in-settings__field"
      >
        <ui-switcher
          v-model="form.isEnableSelfCheckIn"
          :label="$t('form.labels.enableSelfCheckIn')"
          :label-info="$t('checkinSettings.enableSelfCheckIn')"
          class="mb-4"
          :error="errors[0]"
          disabled
        />
      </validation-provider>

      <validation-provider
        v-if="showUniQrSwitcher"
        tag="div"
        :name="$t('form.labels.enableUniQr')"
        mode="passive"
        v-slot="{ errors }"
        class="location-check-in-settings__field"
      >
        <ui-switcher
          v-model="form.enableUniQr"
          :label="$t('form.labels.enableUniQr')"
          :label-info="$t('checkinSettings.enableUniQr')"
          class="mb-4"
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        v-if="isAutomaticCheckout"
        tag="div"
        :name="$t('form.labels.enableQrForceCheckOut')"
        mode="passive"
        v-slot="{ errors }"
        class="location-check-in-settings__field"
      >
        <ui-switcher
          v-model="form.isQrForceCheckOutEnabled"
          :label="$t('form.labels.enableQrForceCheckOut')"
          :label-info="$t('checkinSettings.enableQrForceCheckOut')"
          class="mb-4"
          :error="errors[0]"
        />
      </validation-provider>

      <location-user
        :items="form.checkinManagers"
        :label="$t('form.labels.checkinManagers')"
        :label-info="$t('checkinSettings.enableCheckInManager')"
        :country-code="countryCode"
        :key="`checkinManagers-${countryCode}`"
        :location="form"
        user-role="checkinManagers"
        input-class="mb-0"
        class="location-check-in-settings__field location-check-in-settings__managers"
        :disabled="isPersonal"
        @completed="saveCheckinManagers"
      />

      <validation-provider
        v-if="showServicePrice"
        tag="div"
        :name="$t('form.labels.enableServicePrice')"
        mode="passive"
        v-slot="{ errors }"
        class="location-check-in-settings__field"
      >
        <ui-switcher
          v-model="form.isQrServicePriceEnabled"
          :label="$t('form.labels.enableServicePrice')"
          :label-info="$t('checkinSettings.enableServicePrice')"
          class="mb-4"
          :error="errors[0]"
        />
      </validation-provider>

      <ui-button
        v-if="canDownloadQrCodes"
        action
        round
        type="primary"
        size="sm"
        class="location-check-in-settings__download"
        @click.prevent="openDownloadModal"
      >
        <div class="location-check-in-settings__download-content">
          <ui-icon
            name="download"
            width="24px"
            height="24px"
            class="location-check-in-settings__download-icon"
          />
          <span class="location-check-in-settings__download-text">
            {{ $t('checkinSettings.download.selectButton') }}
          </span>
        </div>
      </ui-button>

      <ui-button
        action
        type="primary"
        :is-loading="loading.form"
        :disabled="loading.form"
        class="location-pay-settings__submit"
        @click.prevent="submit(validate)"
      >
        {{ $t('form.buttons.save') }}
      </ui-button>
    </validation-observer>

    <location-qr-codes-download-modal
      v-if="canDownloadQrCodes"
      v-model="showDownloadModal"
      :workplace-id="location.id"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '@/api';
import LocationQrCodesDownloadModal from '@/components/Location/LocationQrCodesDownloadModal.vue';
import LocationUser from '@/components/Location/LocationUser.vue';

const FORM_DEFAULTS = {
  checkOutSettings: '',
  isEnableSelfCheckIn: false,
  isQrForceCheckOutEnabled: false,
  isQrServicePriceEnabled: false,
  enableUniQr: false,
  checkinManagers: [],
};

const CHECKOUT_VARIANTS = [
  {
    label: 'Automatic checkout',
    value: 'AUTOMATIC_CHECKOUT',
  },
  {
    label: 'Shifts schedule',
    value: 'SHIFT_SCHEDULE',
  },
];

export default {
  name: 'LocationCheckInSettings',

  components: {
    LocationUser,
    LocationQrCodesDownloadModal,
  },

  props: {
    location: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: {
        data: false,
        form: false,
      },
      form: FORM_DEFAULTS,
      CHECKOUT_VARIANTS,
      showDownloadModal: false,
    };
  },

  computed: {
    ...mapGetters('auth', [
      'isAdmin',
      'isSalesPerson',
      'isSupportAdmin',
    ]),
    countryCode() {
      return this.location.country.toUpperCase();
    },
    isPersonal() {
      return this.location.tipsType === 'PERSONAL';
    },
    isCommon() {
      return this.location.tipsType === 'COMMON';
    },
    isAutomaticCheckout() {
      return this.form.checkOutSettings === 'AUTOMATIC_CHECKOUT';
    },
    showUniQrSwitcher() {
      return this.isAutomaticCheckout && !this.isCommon;
    },
    showServicePrice() {
      return this.isAutomaticCheckout
        && this.form.checkinManagers.length;
    },
    canDownloadQrCodes() {
      return this.isAdmin || this.isSalesPerson || this.isSupportAdmin;
    },
  },

  methods: {
    openDownloadModal() {
      this.showDownloadModal = true;
    },

    async loadCheckinManagers() {
      this.loading.data = true;

      try {
        const { data } = await api.checkinManagers.getList({
          locationId: this.location.id,
        });

        if (data) {
          this.form.checkinManagers = [
            ...data
              .filter((item) => item.status !== 'NOT_ACTIVE')
              .map((item) => ({
                ...item,
                oldPhoneNumber: item.phoneNumber,
                oldEmail: item.email,
                newest: false,
                removed: false,
              })),
          ];
        }
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading.data = false;
      }
    },

    async saveCheckinManagers() {
      const list = this.form.checkinManagers;

      const oldManagers = list.filter(({ newest }) => !newest);
      const activeManagers = list.filter(({ removed }) => !removed);

      const oldPhones = new Set(oldManagers.map((m) => m.oldPhoneNumber));
      const activePhones = new Set(activeManagers.map((m) => m.phoneNumber));

      const removeManagers = oldManagers.filter(
        ({ oldPhoneNumber }) => !activePhones.has(oldPhoneNumber),
      );

      const uniqueActiveManagers = Array.from(
        new Map(activeManagers.map(((item) => [item.phoneNumber, item]))).values(),
      );

      const createUsers = uniqueActiveManagers
        .filter(({ phoneNumber }) => !oldPhones.has(phoneNumber));

      await Promise.all([
        ...removeManagers.map(({ id }) => {
          return api.checkinManagers.deactivate({
            id,
            locationId: this.location.id,
          });
        }),
        ...createUsers.map(async ({ phoneNumber, email }) => {
          try {
            await api.checkinManagers.create({
              phoneNumber,
              email,
              locationId: this.location.id,
            });
          } catch (e) {
            if (e?.response?.data?.errorCode !== 422002) {
              throw e;
            }
          }
        }),
      ]);

      return this.loadCheckinManagers();
    },

    async loadCheckInSettings() {
      this.loading.data = true;

      try {
        const { data } = await api.locations.getCheckInSettingsConfig(this.location.id);

        this.form.checkOutSettings = data.checkOutSettings;
        this.form.isEnableSelfCheckIn = this.isPersonal || Boolean(data.isEnableSelfCheckIn);
        this.form.isQrForceCheckOutEnabled = data.isQrForceCheckOutEnabled;
        this.form.isQrServicePriceEnabled = data.isQrServicePriceEnabled;
        this.form.enableUniQr = data.enableUniQr;
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading.data = false;
      }
    },

    async saveCheckInSettings() {
      return api.locations.setCheckInSettingsConfig(
        this.location.id,
        {
          checkOutSettings: this.form.checkOutSettings,
          isEnableSelfCheckIn: this.form.isEnableSelfCheckIn,
          isQrForceCheckOutEnabled: this.form.isQrForceCheckOutEnabled,
          isQrServicePriceEnabled: this.form.isQrServicePriceEnabled,
          enableUniQr: this.form.enableUniQr,
        },
      );
    },

    async submit(validate) {
      if (!await validate()) {
        return;
      }

      this.loading.form = true;

      try {
        await this.saveCheckInSettings();
        await this.saveCheckinManagers();

        this.$showSuccess();
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading.form = false;
      }
    },
  },

  watch: {
    isAutomaticCheckout(newVal) {
      if (!newVal) {
        this.form.isQrForceCheckOutEnabled = false;
        this.form.isQrServicePriceEnabled = false;
      }
    },
    showUniQrSwitcher(newVal) {
      if (!newVal) {
        this.form.enableUniQr = false;
      }
    },
  },

  mounted() {
    this.loadCheckInSettings();
    this.loadCheckinManagers();
  },
};
</script>

<style lang="scss">
.location-check-in-settings {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__wrapper {
    display: flex;
    flex-direction: column;
  }

  &__loader {
    margin: 40px 0;
    position: relative;
  }

  &__field {
    width: 295px;
    max-width: 100%;

    @media screen and (max-width: 991px) {
      width: 100%;
    }

    img {
      width: 100%;
    }
  }

  &__managers .form-group {
    margin-top: 0;
  }

  &__download {
    width: 295px;
    max-width: 100%;

    &-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
  }

  &__submit {
    width: 295px;
    max-width: 100%;
    margin-top: auto;
  }
}
</style>
